import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, adminApi, wsUrl } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import LiveVideo from "../../components/LiveVideo";
import ChatPanel from "../../components/ChatPanel";
import LiveReactions from "../../components/LiveReactions";
import MilestoneStepper from "../../components/MilestoneStepper";
import Countdown from "../../components/Countdown";
import {
  MilestoneStripMini,
  ProductPill,
  StatusPillMini,
  ViewersChip,
  CountdownChip,
} from "../../components/MobileLiveOverlays";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

/**
 * Dedicated Host / Broadcast console.
 * Distinct UI from the viewer LiveRoom — surfaces controls a host needs:
 *  - Step 1: "Enter Live Room" (opens Agora camera)
 *  - Step 2: "Start 2-Minute Live" (kicks off the price-crash timer)
 *  - Step 3: "End Drop" (closes buy window early or on demand)
 * Also shows a full chat moderation panel + milestone / viewer telemetry.
 */
export default function HostLiveRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Co-host mode: admin joins to help moderate chat without publishing any
  // video/audio. Triggered by `/admin/live/:id?role=cohost` (route param) so
  // any admin can jump in without a separate account. The Enter Room hero
  // exposes a "Join as Co-Host" secondary CTA that adds this query param.
  const isCoHost = searchParams.get("role") === "cohost";
  const { admin, loading: authLoading } = useAuth();

  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inRoom, setInRoom] = useState(false);       // host has pressed "Enter Live Room"
  const [publishing, setPublishing] = useState(false); // Agora confirmed publish
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);

  // Viewport-aware mount switch. Both mobile-immersive and desktop layouts
  // each mount a <LiveVideo> that calls getUserMedia + createClient + publish
  // on mount. If both render simultaneously (only CSS-hidden), the host would
  // grab the camera twice and publish TWO audience-facing streams — causing
  // ghost hosts + doubled bandwidth. Solve by picking ONE layout at mount time
  // via matchMedia and re-picking on breakpoint change.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const consecutiveFailsRef = useRef(0);
  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/lives/${id}`);
      setLive(r.data);
      consecutiveFailsRef.current = 0;
    } catch (e) {
      // Only bail out on a genuine 404 (the live was deleted) OR after 3
      // back-to-back failures — a single transient blip must NOT kick the
      // host out of an in-progress broadcast.
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Live not found");
        nav("/admin/lives");
        return;
      }
      consecutiveFailsRef.current += 1;
      if (consecutiveFailsRef.current >= 5) {
        toast.error("Lost connection to the live. Returning to admin.");
        nav("/admin/lives");
      }
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 3000);
    return () => clearInterval(iv);
  }, [refresh]);

  // Waiting-room WebSocket. Opens once we know the live_id and stays open
  // until the drop ends. Reflects the *live* WS headcount (updates via
  // `member_arrival` messages) so the host can watch the pre-show fill up
  // before deciding to press Start. We identify ourselves as member_no=0
  // (special "host observer" sentinel) so we still trigger a broadcast for
  // room-size sync but don't spam other members' arrival toasts.
  const [waitingCount, setWaitingCount] = useState(0);
  const hostWsRef = useRef(null);
  useEffect(() => {
    if (!id) return undefined;
    const ws = new WebSocket(wsUrl(`/api/ws/chat/${id}?member_no=0`));
    hostWsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_e) { return; }
      if (msg?.type === "member_arrival" && typeof msg.room_size === "number") {
        // Subtract the host's own connection so the chip shows *members* waiting.
        setWaitingCount(Math.max(0, msg.room_size - 1));
      }
    };
    return () => {
      try { ws.close(); } catch (_e) { /* noop */ }
      hostWsRef.current = null;
    };
  }, [id]);

  // Redirect if not admin
  useEffect(() => {
    if (!authLoading && !admin) nav("/admin");
  }, [admin, authLoading, nav]);

  const startTimer = async () => {
    if (!publishing) { toast.error("Waiting for your camera to go live..."); return; }
    setStarting(true);
    try {
      await adminApi.post(`/lives/${id}/start`);
      toast.success("Live started — 2-min timer running");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the drop");
    } finally {
      setStarting(false);
    }
  };

  const endDrop = async () => {
    if (!window.confirm("End this drop now? Buy window will close.")) return;
    setEnding(true);
    try {
      await adminApi.post(`/lives/${id}/end`);
      toast.success("Drop ended");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not end drop");
    } finally {
      setEnding(false);
    }
  };

  if (loading || !live) {
    return <div className="min-h-screen flex items-center justify-center font-mono-ds text-[#6E6E6E]">// Loading host console...</div>;
  }

  const status = live.status;
  const isLive = status === "live";
  const isFinalWindow = status === "final_window";
  const isEnded = status === "ended";
  const isScheduled = status === "scheduled";
  const isOpen = status === "open";

  return (
    <div className="min-h-screen bg-[#F8F7F2]" data-testid="host-live-room">
      {/* Host header bar */}
      <div className="border-b-2 border-[#FF6A00] bg-[#F8F7F2]/95 backdrop-blur sticky top-0 z-30">
        <div className="px-4 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/admin/lives"
              className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E] hover:text-[#FF6A00] whitespace-nowrap"
              data-testid="host-back-link"
            >
              ← Lives
            </Link>
            <div className="hidden md:block w-px h-6 bg-[#E5E1D8]" />
            <div className="min-w-0">
              <div className="brand-tag">◆ HOST CONSOLE</div>
              <div className="font-display text-lg md:text-xl uppercase tracking-tight truncate" data-testid="host-live-title">
                {live.title}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <HostStatusPill status={status} inRoom={inRoom} publishing={publishing} />
            <ViewersCounter n={live.participants || 0} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 md:px-8 py-4 md:py-6">
        {/* Co-host mode — chat moderation only, no video/audio publishing */}
        {isCoHost && (
          <CoHostConsole
            live={live}
            admin={admin}
            status={status}
            waitingCount={waitingCount}
          />
        )}

        {/* State 1: Not yet in the room → big Enter CTA hero */}
        {!isCoHost && !inRoom && !isEnded && (
          <EnterRoomHero
            live={live}
            liveId={id}
            isScheduled={isScheduled}
            isOpen={isOpen}
            waitingCount={waitingCount}
            onEnter={() => { setInRoom(true); toast.success("Camera going live — start the drop when ready"); }}
          />
        )}

        {/* State 2/3/4: In the room → broadcast console */}
        {!isCoHost && (inRoom || isEnded) && !isDesktop && (
          <>
            {/* ============ MOBILE IMMERSIVE (host) ============ */}
            <HostMobileImmersive
              live={live}
              admin={admin}
              status={status}
              inRoom={inRoom}
              publishing={publishing}
              starting={starting}
              ending={ending}
              onStart={startTimer}
              onEnd={endDrop}
              onRefresh={refresh}
            />
          </>
        )}
        {!isCoHost && (inRoom || isEnded) && isDesktop && (
          <>
            {/* ============ DESKTOP LAYOUT ============ */}
            <div className="grid grid-cols-[1fr_360px] gap-4">
            {/* LEFT — video + controls + product */}
            <div className="flex flex-col gap-4 min-w-0">
              {/* Video preview */}
              <div className="aspect-video relative border border-[#E5E1D8] bg-[#F8F7F2]">
                {isEnded ? (
                  <EndedPanel live={live} />
                ) : (
                  <LiveVideo
                    channel={live.channel}
                    mode="host"
                    autoJoin
                    onHostPublished={() => setPublishing(true)}
                  />
                )}
                {/* Big countdown when live */}
                {(isLive || isFinalWindow) && (
                  <BigCountdown
                    to={isLive ? live.live_ends_at : live.final_window_ends_at}
                    label={isLive ? "LIVE ENDS IN" : "BUY WINDOW"}
                    onComplete={refresh}
                  />
                )}
                {/* Audience reactions floating overlay on the host video */}
                {(isLive || isFinalWindow) && (
                  <LiveReactions liveId={live.live_id} memberNo={0} position="bottom-right" hostLabel />
                )}
              </div>

              {/* Broadcast action strip */}
              <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="brand-tag">// BROADCAST</div>
                  <div className="font-display text-lg uppercase tracking-tight mt-1 break-words">
                    {isEnded
                      ? "Drop ended · Archive saved"
                      : isLive
                        ? "You are LIVE — 2-minute drop running"
                        : isFinalWindow
                          ? "Final price locked · 15-min buy window open"
                          : publishing
                            ? "Camera live · Press Start when ready"
                            : "Waiting for camera to connect..."}
                  </div>
                  <div className="mt-1 font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E]">
                    {isLive || isFinalWindow
                      ? "Chat is OPEN — answer viewer questions in the panel →"
                      : isEnded
                        ? `${live.participants} attended · ₹${live.current_price} final`
                        : "Timer starts when you press the button below"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {!isLive && !isFinalWindow && !isEnded && (
                    <Button
                      onClick={startTimer}
                      disabled={!publishing || starting}
                      className="rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase text-sm tracking-widest h-12 px-6 font-bold disabled:bg-[#E5E1D8] disabled:text-[#6E6E6E] disabled:cursor-not-allowed"
                      data-testid="host-start-2min-btn"
                    >
                      {starting ? "Starting..." : publishing ? "▶ Start 2-Minute Live" : "Waiting for camera..."}
                    </Button>
                  )}
                  {(isLive || isFinalWindow) && (
                    <Button
                      onClick={endDrop}
                      disabled={ending}
                      className="rounded-2xl bg-[#FFFFFF] border border-[#FF6A00] hover:bg-[#FF6A00] hover:text-[#161616] text-[#FF6A00] font-mono-ds uppercase text-sm tracking-widest h-12 px-6 font-bold"
                      data-testid="host-end-drop-btn"
                    >
                      {ending ? "Ending..." : "End Drop"}
                    </Button>
                  )}
                  {isEnded && (
                    <Link
                      to="/admin/lives"
                      className="inline-flex items-center justify-center rounded-2xl bg-[#FFFFFF] hover:bg-[#FF6A00] hover:text-[#161616] text-[#161616] font-mono-ds uppercase text-sm tracking-widest h-12 px-6 font-bold"
                      data-testid="host-back-to-lives"
                    >
                      Back to Lives →
                    </Link>
                  )}
                </div>
              </div>

              {/* Product + Milestones */}
              <div className="grid md:grid-cols-2 gap-4">
                <ProductPanel product={live.product} currentPrice={live.current_price} />
                <MilestoneStepper
                  milestones={live.milestones}
                  viewers={live.participants}
                  currentPrice={live.current_price}
                />
              </div>
            </div>

            {/* RIGHT — chat moderation panel */}
            <div className="min-h-[520px] lg:min-h-0">
              <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-3 mb-2">
                <div className="brand-tag">// MODERATION</div>
                <div className="font-mono-ds text-[11px] text-[#6E6E6E] mt-1 leading-snug">
                  You post as <span className="text-[#FF6A00] font-bold">★ {admin?.email?.split("@")[0] || "Host"}</span>.
                  Replies show a red host badge to viewers.
                </div>
              </div>
              <div className="h-[520px] lg:h-[calc(100vh-260px)]">
                <ChatPanel
                  liveId={live.live_id}
                  liveStatus={live.status}
                  userName={admin?.email?.split("@")[0] || "Host"}
                  isHost
                />
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------- Pre-room hero (Enter Live Room) -------------------- */
function EnterRoomHero({ live, liveId, isScheduled, isOpen, waitingCount = 0, onEnter }) {
  const scheduledAt = new Date(live.scheduled_at).getTime();
  const now = Date.now();
  const LEAD_MS = 5 * 60 * 1000;
  const isLive = live.status === "live";
  const isFinalWindow = live.status === "final_window";
  const canEnter = isOpen || isLive || isFinalWindow || (isScheduled && now >= scheduledAt - LEAD_MS);
  const secsUntilOpen = Math.max(0, Math.floor((scheduledAt - LEAD_MS - now) / 1000));

  return (
    <div className="border border-[#E5E1D8] bg-[#FFFFFF]" data-testid="host-pre-room-hero">
      <div className="grid lg:grid-cols-[1fr_420px] gap-0">
        {/* Left: Big call-to-action */}
        <div className="p-8 md:p-12 grid-bg border-b lg:border-b-0 lg:border-r border-[#E5E1D8]">
          <div className="brand-tag">◆ HOST · PRE-ROOM</div>
          <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.95]">
            You&apos;re the Host.<br />
            <span className="text-[#FF6A00]">Ready to go live?</span>
          </h1>
          <p className="mt-4 max-w-lg text-[#6E6E6E] text-sm leading-relaxed">
            Entering the room will publish your camera + mic to the channel. Viewers will see your video, but the 2-minute drop timer only starts when <span className="text-[#161616] font-bold">you</span> press &quot;Start Live&quot;.
          </p>

          {/* Checklist */}
          <ul className="mt-6 space-y-2 font-mono-ds text-xs uppercase tracking-widest text-[#6E6E6E]">
            <li className="flex items-center gap-2"><Bullet ok /> Camera + microphone permissions</li>
            <li className="flex items-center gap-2"><Bullet ok /> Milestones locked ({live.milestones?.length || 0} tiers)</li>
            <li className="flex items-center gap-2"><Bullet ok /> Product stock ready ({live.product?.stock ?? 0} units)</li>
            <li className="flex items-center gap-2">
              <Bullet ok={canEnter} />
              {canEnter ? "Broadcast window open" : `Opens ${formatWait(secsUntilOpen)} before scheduled time`}
            </li>
          </ul>

          {/* Countdown to scheduled time */}
          {isScheduled && (
            <div className="mt-8">
              <div className="brand-tag mb-2">// SCHEDULED START</div>
              <Countdown to={live.scheduled_at} />
            </div>
          )}

          {/* THE ENTER BUTTON */}
          <div className="mt-8">
            {/* Waiting-room chip — mirrors the PreLive arrival wave so the host
                can see the room fill before pressing Enter. Ticks up as members
                connect via WebSocket. */}
            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 font-mono-ds text-[11px] uppercase tracking-[0.24em] mb-3 border ${waitingCount > 0 ? "border-[#264733] bg-[#B9E641]/40 text-[#264733] font-bold" : "border-[#E5E1D8] bg-[#F8F7F2] text-[#6E6E6E]"}`}
              data-testid="host-waiting-chip"
              title={waitingCount > 0 ? "Members already in the waiting room" : "Waiting room is empty"}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${waitingCount > 0 ? "bg-[#264733] animate-pulse" : "bg-[#6E6E6E]"}`} />
              <span className="tabular-nums">{waitingCount}</span>
              <span>{waitingCount === 1 ? "member" : "members"} waiting</span>
            </div>
            <Button
              onClick={onEnter}
              disabled={!canEnter}
              className="rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase text-base md:text-lg tracking-widest h-14 md:h-16 px-8 font-bold disabled:bg-[#E5E1D8] disabled:text-[#6E6E6E] disabled:cursor-not-allowed"
              data-testid="host-enter-room-btn"
            >
              {canEnter ? "▶ Enter Live Room" : "Room Opens Soon..."}
            </Button>
            {/* Co-host secondary CTA — join to moderate chat without publishing
                video/audio. Same URL, adds ?role=cohost so any admin can jump in. */}
            <Link
              to={`/admin/live/${liveId}?role=cohost`}
              className="ml-3 inline-flex items-center gap-2 border border-[#161616] hover:bg-[#161616] hover:text-white text-[#161616] font-mono-ds uppercase text-xs md:text-sm tracking-widest h-14 md:h-16 px-6 font-bold transition-colors"
              data-testid="host-join-cohost-btn"
            >
              ★ Join as Co-Host
            </Link>
            <div className="mt-3 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
              Your camera activates as soon as you enter · timer stays paused until you press Start · Co-hosts moderate chat only
            </div>
          </div>
        </div>

        {/* Right: Product snapshot */}
        <aside className="p-6 md:p-8 bg-[#FFFFFF]/60">
          <div className="brand-tag">// TONIGHT&apos;S DROP</div>
          {live.product?.image_url && (
            <div className="aspect-square bg-[#FFFFFF] overflow-hidden mt-3 border border-[#E5E1D8]">
              <img src={live.product.image_url} alt={live.product.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-4 leading-tight">
            {live.product?.name}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1 font-mono-ds">
            <MiniPrice label="MRP" value={live.product?.mrp} strike />
            <MiniPrice label="Start" value={live.product?.starting_price || live.product?.base_price} accent />
            <MiniPrice label="Floor" value={live.product?.floor_price} highlight />
          </div>
          <div className="mt-4 font-mono-ds text-[11px] text-[#6E6E6E] leading-snug">
            {live.milestones?.length || 0} viewer milestones · price drops as the room fills.
          </div>
        </aside>
      </div>
    </div>
  );
}

/* -------------------- Sub-components -------------------- */

/**
 * Co-host console — lean moderation-only view accessed via
 * `/admin/live/:id?role=cohost`. No video/audio publishing; the co-host just
 * gets the chat panel with pin/delete power actions and a small header for
 * lifecycle awareness. Any admin can jump in without a separate account.
 */
function CoHostConsole({ live, admin, status, waitingCount }) {
  const isLive = status === "live";
  const isFinalWindow = status === "final_window";
  const isEnded = status === "ended";
  return (
    <div className="max-w-4xl mx-auto" data-testid="cohost-console">
      <div className="border border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#264733] p-4 md:p-6 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="brand-tag mb-1">// Co-host console</div>
            <div className="font-display text-xl md:text-2xl uppercase tracking-tight leading-tight">
              Moderating <span className="text-outline">chat only</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono-ds text-[10px] uppercase tracking-[0.24em] px-2 py-1 border ${
              isLive ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30] font-bold"
              : isFinalWindow ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00] font-bold"
              : isEnded ? "border-[#6E6E6E] text-[#6E6E6E]" : "border-[#264733] text-[#264733]"
            }`}>
              {isLive ? "● Live Now" : isFinalWindow ? "◆ Final Window" : isEnded ? "◇ Ended" : "◇ Waiting"}
            </span>
            <span className="font-mono-ds text-[10px] uppercase tracking-[0.24em] px-2 py-1 border border-[#E5E1D8] bg-[#F8F7F2] text-[#6E6E6E]">
              <span className="tabular-nums text-[#161616]">{waitingCount}</span> waiting
            </span>
          </div>
        </div>
        <p className="mt-3 text-xs text-[#6E6E6E] font-mono-ds">
          As a co-host you can <span className="text-[#161616] font-bold">pin</span> or <span className="text-[#161616] font-bold">delete</span> any message. You won&apos;t appear on the video. Signed in as <span className="text-[#161616] font-bold">{admin?.email || admin?.name || "admin"}</span>.
        </p>
      </div>

      {/* Pre-drop demand signals — how many people are peeking at the drop
          page + how many liked it. Feeds the cohost's decision to crash
          prices manually. Only meaningful while the drop is scheduled/open. */}
      <PreDropStats live={live} />

      {/* Co-host milestone control — drag thresholds to release price tiers early as a surprise for Founding Members. */}
      <MilestoneControl live={live} />

      <div className="border border-[#161616] bg-[#F8F7F2] h-[70vh] min-h-[520px] font-mono-ds text-[12px]" data-testid="cohost-chat">
        <ChatPanel liveId={live.live_id} liveStatus={live.status} userName={admin?.name ? `Co-Host ${admin.name.split(" ")[0]}` : "Co-Host"} isHost />
      </div>
    </div>
  );
}

/**
 * Milestone Control — 5 dual-thumb sliders letting the co-host manually drag
 * the participant threshold at which each price tier fires. Lowering a
 * threshold below the current participant count triggers an INSTANT price
 * crash for everyone in the room (surprise mechanic for Founding Members).
 *
 * Never shown to viewers. The bar we send to the backend is broadcast on the
 * chat WS as `{type: "milestones_updated"}`, and viewers' 3-second polling
 * loop picks up the new thresholds on their next tick.
 */
function MilestoneControl({ live }) {
  const initialThresholds = useMemo(() => (live?.milestones || []).map((m) => m.threshold), [live?.live_id]);
  const [thresholds, setThresholds] = useState(initialThresholds);
  const [saving, setSaving] = useState(false);
  const [lastPushed, setLastPushed] = useState(null);
  const pushTimer = useRef(null);

  // If milestones arrive via polling AFTER first render, seed state from them.
  // We only overwrite local state when the array from the server actually
  // differs from what we have — otherwise every 3s poll would clobber a drag.
  useEffect(() => {
    const next = (live?.milestones || []).map((m) => m.threshold);
    if (!next.length) return;
    setThresholds((prev) => (
      prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next
    ));
  }, [live?.milestones?.map((m) => m.threshold).join(",")]);

  const prices = (live?.milestones || []).map((m) => m.price);
  const participants = live?.participants || 0;
  // Effective viewer price = highest-price tier whose threshold ≤ participants
  const effectivePrice = (() => {
    const rows = thresholds.map((t, i) => ({ threshold: t, price: prices[i] }));
    const sorted = [...rows].sort((a, b) => a.threshold - b.threshold);
    let p = sorted[0]?.price ?? prices[0] ?? 0;
    for (const r of sorted) if (participants >= r.threshold) p = r.price;
    return p;
  })();

  const push = useCallback(async (nextThresholds) => {
    if (!live?.live_id) return;
    setSaving(true);
    try {
      const payload = {
        milestones: nextThresholds.map((t, i) => ({ threshold: Math.max(0, Math.min(250, Math.round(t))), price: prices[i] })),
      };
      await adminApi.patch(`/lives/${live.live_id}/milestones`, payload);
      setLastPushed(Date.now());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Milestone update failed");
    } finally {
      setSaving(false);
    }
  }, [live?.live_id, prices]);

  const onSlide = (i, val) => {
    setThresholds((prev) => {
      const next = prev.slice();
      next[i] = val;
      // Enforce monotonic non-decreasing ordering — bumping T2 to 30 also
      // bumps T3 up to 30 if it was smaller, and so on.
      for (let k = i + 1; k < next.length; k += 1) {
        if (next[k] < next[k - 1]) next[k] = next[k - 1];
      }
      for (let k = i - 1; k >= 0; k -= 1) {
        if (next[k] > next[k + 1]) next[k] = next[k + 1];
      }
      return next;
    });
    // Debounce push so a fast drag doesn't hammer the backend
    if (pushTimer.current) clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(() => {
      setThresholds((current) => { push(current); return current; });
    }, 250);
  };

  // One-tap: force this tier + all earlier tiers to threshold=1 so they fire
  // instantly for the current room, regardless of participant count.
  const fireNow = (i) => {
    setThresholds((prev) => {
      const next = prev.slice();
      // Set to 0 so tier fires even when the room is empty (force crash).
      for (let k = 0; k <= i; k += 1) next[k] = 0;
      // Enforce monotonic ordering for later tiers
      for (let k = i + 1; k < next.length; k += 1) {
        if (next[k] < next[k - 1]) next[k] = next[k - 1];
      }
      return next;
    });
    if (pushTimer.current) clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(() => {
      setThresholds((current) => { push(current); return current; });
    }, 100);
  };

  if (!live?.milestones?.length) return null;
  return (
    <div className="border border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#FF6A00] p-4 md:p-6 mb-4" data-testid="cohost-milestone-control">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="brand-tag mb-1 text-[#FF6A00]">// Manual price crash · Co-host only</div>
          <div className="font-display text-lg md:text-xl uppercase tracking-tight leading-tight">
            Drag a tier down to <span className="text-outline">release the price.</span>
          </div>
        </div>
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">
          {saving ? "Saving…" : lastPushed ? `Broadcast · ${new Date(lastPushed).toLocaleTimeString()}` : "Idle"}
          <span className="opacity-40 mx-2">·</span>
          <span className="text-[#161616] font-bold">{participants}</span> in room
        </div>
      </div>

      {/* Viewer's current effective price — what everyone in the room sees right now. */}
      <div className="mb-4 border-2 border-[#161616] bg-[#161616] text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="cohost-effective-price">
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#B9E641]">
          ● Viewers see right now
        </div>
        <div className="flex items-center gap-3">
          <span className="font-display text-3xl tabular-nums text-[#FF6A00] font-bold" data-testid="cohost-effective-price-value">₹{effectivePrice}</span>
          <span className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-white/50">
            Refreshes for viewers within 3s of a change
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {thresholds.map((t, i) => {
          const active = participants >= t;
          const price = prices[i];
          return (
            <div key={i} className="grid grid-cols-12 gap-3 items-center" data-testid={`cohost-milestone-row-${i + 1}`}>
              <div className="col-span-2 md:col-span-1 font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E] font-bold">
                T{i + 1}
              </div>
              <div className="col-span-3 md:col-span-2 font-mono-ds text-[13px] tabular-nums font-bold text-[#161616]">
                ₹{price}
              </div>
              <div className="col-span-5 md:col-span-5">
                <input
                  type="range"
                  min={0}
                  max={250}
                  value={t}
                  onChange={(e) => onSlide(i, Number(e.target.value))}
                  className={`w-full h-2 accent-[#FF6A00] cursor-pointer ${active ? "opacity-100" : "opacity-90"}`}
                  data-testid={`cohost-milestone-slider-${i + 1}`}
                />
              </div>
              <div className="col-span-2 md:col-span-2 flex items-center justify-end gap-2 font-mono-ds text-[11px] uppercase tracking-widest">
                <span className="tabular-nums text-[#161616] font-bold">≥ {t}</span>
                <span className={`px-1.5 py-0.5 border text-[9px] ${active ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00] font-bold" : "border-[#E5E1D8] text-[#6E6E6E]"}`}>
                  {active ? "● Fired" : "◇ Armed"}
                </span>
              </div>
              <div className="col-span-12 md:col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => fireNow(i)}
                  disabled={active}
                  className={`h-8 px-2 border font-mono-ds text-[10px] uppercase tracking-widest transition-colors ${
                    active
                      ? "border-[#E5E1D8] text-[#6E6E6E] cursor-not-allowed"
                      : "border-[#161616] bg-[#FF6A00] text-[#161616] font-bold hover:bg-[#161616] hover:text-[#FF6A00]"
                  }`}
                  data-testid={`cohost-milestone-fire-${i + 1}`}
                  title={active ? "This tier already fired" : `Fire ₹${price} instantly for viewers`}
                >
                  {active ? "Fired" : `Fire ₹${price} →`}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-[#E5E1D8] font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
        ◇ Slider LEFT = fires sooner (fewer viewers needed) · Tap <span className="text-[#161616] font-bold">Fire ₹</span> to crash to that price for everyone in the room instantly · Viewers refresh within 3s.
      </div>
    </div>
  );
}

function HostStatusPill({ status, inRoom, publishing }) {
  const map = {
    scheduled: { label: "SCHEDULED", cls: "border-[#E5E1D8] text-[#6E6E6E]" },
    open: { label: "OPEN · WAITING", cls: "border-[#FF6A00] text-[#FF6A00]" },
    live: { label: "● LIVE", cls: "border-[#FF6A00] bg-[#FF6A00] text-[#161616] font-bold" },
    final_window: { label: "FINAL WINDOW", cls: "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" },
    ended: { label: "ENDED", cls: "border-[#E5E1D8] text-[#6E6E6E]" },
  };
  const m = map[status] || map.scheduled;
  const roomState = inRoom
    ? (publishing ? "CAM LIVE" : "CONNECTING...")
    : "NOT IN ROOM";
  return (
    <div className="flex items-center gap-2">
      <div className={`font-mono-ds text-[10px] uppercase tracking-widest px-2 py-1 border ${m.cls}`} data-testid={`host-status-${status}`}>
        {m.label}
      </div>
      <div
        className={`font-mono-ds text-[10px] uppercase tracking-widest px-2 py-1 border ${
          inRoom && publishing
            ? "border-green-500 text-green-400"
            : inRoom
              ? "border-yellow-500 text-yellow-400"
              : "border-[#E5E1D8] text-[#6E6E6E]"
        }`}
        data-testid="host-cam-state"
      >
        {roomState}
      </div>
    </div>
  );
}

function ViewersCounter({ n }) {
  return (
    <div className="font-mono-ds text-[10px] uppercase tracking-widest px-2 py-1 border border-[#E5E1D8] text-[#6E6E6E] flex items-center gap-1.5" data-testid="host-viewers-count">
      <span className="w-1.5 h-1.5 bg-[#FF6A00] pulse-dot" />
      <span className="text-[#161616] font-bold tabular-nums">{n.toLocaleString()}</span>
      <span>viewers</span>
    </div>
  );
}

function ProductPanel({ product, currentPrice }) {
  if (!product) return null;
  return (
    <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-4 flex gap-3 min-w-0" data-testid="host-product-panel">
      {product.image_url && (
        <div className="w-24 h-24 md:w-28 md:h-28 bg-[#FFFFFF] flex-shrink-0 overflow-hidden">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="min-w-0 flex flex-col">
        <div className="brand-tag">// PRODUCT</div>
        <div className="font-display text-lg md:text-xl uppercase tracking-tight leading-tight mt-1 break-words line-clamp-2">
          {product.name}
        </div>
        <div className="mt-auto pt-2 flex items-baseline gap-3">
          <span className="font-mono-ds text-2xl font-bold text-[#FF6A00] tabular-nums">
            ₹{Number(currentPrice || 0).toLocaleString("en-IN")}
          </span>
          <span className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
            Active price
          </span>
        </div>
        <div className="mt-1 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
          Stock: <span className={`font-bold ${product.stock <= 0 ? "text-[#FF6A00]" : product.stock <= 10 ? "text-yellow-400" : "text-[#161616]"}`}>{product.stock ?? 0}</span>
        </div>
      </div>
    </div>
  );
}

function BigCountdown({ to, label, onComplete }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const target = new Date(to).getTime();
  const diff = Math.max(0, target - now);
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  useEffect(() => {
    if (diff <= 0 && onComplete) onComplete();
  }, [diff, onComplete]);
  return (
    <div
      className="absolute top-4 right-4 border-2 border-[#FF6A00] bg-[#F8F7F2]/90 px-4 py-2 shadow-[0_4px_12px_rgba(17,17,17,0.06)] backdrop-blur"
      data-testid="host-big-countdown"
    >
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.3em] text-[#FF6A00] font-bold">{label}</div>
      <div className="font-display text-4xl md:text-5xl leading-none tracking-tighter tabular-nums mt-1 text-[#161616]">
        {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
      </div>
    </div>
  );
}

function EndedPanel({ live }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-[#FFFFFF]">
      <div className="text-center">
        <div className="brand-tag mb-3">// DROP ARCHIVED</div>
        <div className="font-display text-4xl md:text-5xl uppercase tracking-tighter">Session Ended</div>
        <div className="mt-3 font-mono-ds text-sm text-[#6E6E6E]">
          {live.participants} viewers · Final price ₹{Number(live.current_price || 0).toLocaleString("en-IN")}
        </div>
      </div>
    </div>
  );
}

function Bullet({ ok }) {
  return (
    <span
      className={`inline-block w-2 h-2 ${ok ? "bg-green-400 shadow-[0_2px_6px_rgba(22,163,74,0.15)]" : "bg-[#E5E1D8]"}`}
      aria-hidden
    />
  );
}

function MiniPrice({ label, value, strike, accent, highlight }) {
  const valColor = highlight
    ? "text-[#FF6A00]"
    : strike
      ? "text-[#6E6E6E] line-through"
      : accent
        ? "text-[#161616]"
        : "text-[#6E6E6E]";
  return (
    <div className={`border ${accent ? "border-[#FF6A00]/60" : "border-[#E5E1D8]"} bg-[#FFFFFF]/70 px-2 py-1.5 flex flex-col leading-tight min-w-0`}>
      <span className="text-[9px] uppercase tracking-[0.15em] text-[#6E6E6E] font-bold">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${valColor} truncate`}>
        {value ? `₹${Number(value).toLocaleString("en-IN")}` : "—"}
      </span>
    </div>
  );
}

function formatWait(secs) {
  if (!secs || secs <= 0) return "now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rm = mins % 60;
  return rm ? `in ${hrs}h ${rm}m` : `in ${hrs}h`;
}


/* -------------------- Mobile immersive host layout -------------------- */
function HostMobileImmersive({ live, admin, status, inRoom, publishing, starting, ending, onStart, onEnd, onRefresh }) {
  const isLive = status === "live";
  const isFinalWindow = status === "final_window";
  const isEnded = status === "ended";

  return (
    <div
      className="lg:hidden fixed inset-0 z-40 bg-[#F8F7F2] text-[#161616] overflow-hidden"
      style={{ height: "100dvh" }}
      data-testid="host-live-room-mobile"
    >
      {/* Base: host video preview */}
      <div className="absolute inset-0">
        {isEnded ? (
          <div className="w-full h-full flex items-center justify-center bg-[#FFFFFF]">
            <div className="text-center px-6">
              <div className="brand-tag mb-2">// DROP ARCHIVED</div>
              <div className="font-display text-3xl uppercase tracking-tighter">Session Ended</div>
              <div className="mt-2 font-mono-ds text-xs text-[#6E6E6E]">
                {live.participants} viewers · Final ₹{Number(live.current_price || 0).toLocaleString("en-IN")}
              </div>
            </div>
          </div>
        ) : (
          <LiveVideo
            channel={live.channel}
            mode="host"
            autoJoin
            onHostPublished={() => { /* parent already tracks via prop */ }}
          />
        )}
      </div>

      {/* Gradients for readability */}
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#F8F7F2]/95 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#F8F7F2]/95 via-[#F8F7F2]/70 to-transparent pointer-events-none" />

      {/* Audience reactions overlay (mobile host) */}
      {(isLive || isFinalWindow) && (
        <LiveReactions liveId={live.live_id} memberNo={0} position="bottom-right" compact hostLabel bottomPx={140} />
      )}

      {/* Top bar */}
      <div
        className="absolute top-0 inset-x-0 z-20 flex items-center justify-between gap-2 px-2.5"
        style={{ paddingTop: "max(10px, env(safe-area-inset-top))" }}
      >
        <Link
          to="/admin/lives"
          className="w-9 h-9 flex items-center justify-center border border-[#E5E1D8] bg-[#FFFFFF]/85 backdrop-blur text-[#161616] hover:text-[#FF6A00] hover:border-[#FF6A00]"
          data-testid="host-back-link-mobile"
          aria-label="Back to lives"
        >
          ←
        </Link>
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <div className="brand-tiny text-[#FF6A00] font-bold whitespace-nowrap">◆ HOST</div>
          <div className="font-display text-xs uppercase tracking-tight truncate min-w-0 text-[#161616]/90">
            {live.title}
          </div>
        </div>
        <StatusPillMini status={status} />
        <ViewersChip n={live.participants} />
      </div>

      {/* Cam-state pill (host-only) below top bar */}
      <div className="absolute top-14 left-2.5 z-20">
        <div
          className={`font-mono-ds text-[10px] uppercase tracking-widest px-2 py-1 border backdrop-blur bg-[#FFFFFF]/85 ${
            publishing
              ? "border-green-500 text-green-400"
              : inRoom
                ? "border-yellow-500 text-yellow-400"
                : "border-[#E5E1D8] text-[#6E6E6E]"
          }`}
          data-testid="host-cam-state-mobile"
        >
          {publishing ? "● CAM LIVE" : inRoom ? "CONNECTING..." : "NOT IN ROOM"}
        </div>
      </div>

      {/* Countdown top-right */}
      {(isLive || isFinalWindow) && (
        <div className="absolute top-14 right-2.5 z-20">
          <CountdownChip
            to={isLive ? live.live_ends_at : live.final_window_ends_at}
            label={isLive ? "LIVE ENDS" : "BUY CLOSES"}
            onComplete={onRefresh}
          />
        </div>
      )}

      {/* Middle-left overlays: product + milestones */}
      <div
        className="absolute z-20 left-2.5 right-2.5 pointer-events-none"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 240px)" }}
      >
        <div className="flex flex-col gap-2 items-start pointer-events-auto">
          <ProductPill product={live.product} />
          <MilestoneStripMini
            milestones={live.milestones}
            viewers={live.participants}
            currentPrice={live.current_price}
          />
          <div className="brand-tiny bg-[#FFFFFF]/85 backdrop-blur border border-[#E5E1D8] px-2 py-1" data-testid="host-stock-mobile">
            Stock:{" "}
            <span className={`font-bold ${live.product?.stock <= 0 ? "text-[#FF6A00]" : live.product?.stock <= 10 ? "text-yellow-400" : "text-[#161616]"}`}>
              {live.product?.stock ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom stack: chat + broadcast CTA */}
      <div
        className="absolute z-20 inset-x-0 bottom-0 flex flex-col"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Moderation banner */}
        <div className="bg-[#FF6A00]/10 backdrop-blur border-t border-[#FF6A00]/40 px-3 py-1 flex items-center justify-between">
          <div className="brand-tiny text-[#FF6A00]">
            ◆ Moderating as ★ {admin?.email?.split("@")[0] || "Host"}
          </div>
        </div>

        {/* Chat overlay */}
        <ChatPanel
          liveId={live.live_id}
          liveStatus={live.status}
          userName={admin?.email?.split("@")[0] || "Host"}
          isHost
          overlay
        />

        {/* Host action bar */}
        <div className="px-2.5 py-2 bg-[#FFFFFF]/90 backdrop-blur border-t border-[#E5E1D8]">
          {!isLive && !isFinalWindow && !isEnded && (
            <Button
              onClick={onStart}
              disabled={!publishing || starting}
              className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-12 font-bold disabled:bg-[#E5E1D8] disabled:text-[#6E6E6E] disabled:cursor-not-allowed"
              data-testid="host-start-2min-btn-mobile"
            >
              {starting ? "Starting..." : publishing ? "▶ Start 2-Minute Live" : "Waiting for Camera..."}
            </Button>
          )}
          {(isLive || isFinalWindow) && (
            <Button
              onClick={onEnd}
              disabled={ending}
              className="w-full rounded-2xl bg-[#FFFFFF] border border-[#FF6A00] hover:bg-[#FF6A00] hover:text-[#161616] text-[#FF6A00] font-mono-ds uppercase tracking-widest h-12 font-bold"
              data-testid="host-end-drop-btn-mobile"
            >
              {ending ? "Ending..." : "End Drop"}
            </Button>
          )}
          {isEnded && (
            <Link
              to="/admin/lives"
              className="w-full rounded-2xl bg-[#FFFFFF] hover:bg-[#FF6A00] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-12 font-bold flex items-center justify-center"
              data-testid="host-back-to-lives-mobile"
            >
              Back to Lives →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * PreDropStats — cohost signal panel showing demand for the upcoming drop.
 *
 * Displays unique viewers on the drop page, total likes, and last-hour
 * momentum. No hard target: the cohost uses this as vibes-data to decide
 * how aggressively to crash the price via MilestoneControl.
 *
 * Auto-refreshes every 10s. Hidden entirely once the drop has ended.
 */
function PreDropStats({ live }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!live?.live_id) return undefined;
    let cancelled = false;
    const tick = () => {
      adminApi.get(`/admin/lives/${live.live_id}/pre-drop-stats`)
        .then((r) => { if (!cancelled) setStats(r.data); })
        .catch(() => { /* silent */ });
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [live?.live_id]);

  if (!live?.live_id || live?.status === "ended") return null;
  if (!stats) return null;

  const series = stats.viewers_series_24h || [];
  const peak = Math.max(1, ...series);

  return (
    <div className="border border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#264733] p-4 md:p-6 mb-4" data-testid="cohost-predrop-stats">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="brand-tag text-[#264733] mb-1">// Pre-drop demand · Co-host only</div>
          <div className="font-display text-lg md:text-xl uppercase tracking-tight leading-tight">
            Who&apos;s <span className="text-outline">watching the drop.</span>
          </div>
        </div>
        <span className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">
          24H window · auto-refresh
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Unique viewers" value={stats.unique_viewers} testid="stat-viewers" />
        <StatTile label="Total likes" value={stats.likes} testid="stat-likes" accent="#FF3B30" />
        <StatTile label="Viewers · last hr" value={stats.viewers_last_hour} testid="stat-viewers-hr" hint={stats.viewers_last_hour > 0 ? "warm" : "cold"} />
        <StatTile label="Likes · last hr" value={stats.likes_last_hour} testid="stat-likes-hr" accent="#FF3B30" hint={stats.likes_last_hour > 0 ? "warm" : "cold"} />
      </div>

      {/* Sparkline — viewer arrivals per hour over the last 24h. */}
      <div className="mt-4 border border-[#E5E1D8] bg-[#F8F7F2] p-3" data-testid="predrop-sparkline">
        <div className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#6E6E6E] mb-2">
          Arrivals · last 24 hours (bar per hour)
        </div>
        <div className="flex items-end gap-[2px] h-14">
          {series.map((n, i) => (
            <div
              key={i}
              className="flex-1 bg-[#264733] transition-all"
              style={{ height: `${(n / peak) * 100}%`, minHeight: n > 0 ? 3 : 1 }}
              title={`${i}h ago · ${n} arrivals`}
            />
          ))}
        </div>
      </div>

      <p className="mt-3 font-mono-ds text-[10px] uppercase tracking-[0.20em] text-[#6E6E6E]">
        No target — just vibes. Use this to gauge whether to crash prices harder tonight.
      </p>
    </div>
  );
}

function StatTile({ label, value, testid, accent = "#161616", hint }) {
  const warm = hint === "warm";
  return (
    <div className="border-2 border-[#161616] bg-[#F8F7F2] p-3" data-testid={testid}>
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#6E6E6E]">{label}</div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="font-display text-3xl tabular-nums font-bold" style={{ color: accent }}>
          {Number(value || 0).toLocaleString("en-IN")}
        </span>
        {hint && (
          <span className={`font-mono-ds text-[9px] uppercase tracking-[0.24em] px-1.5 py-0.5 border ${warm ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00] font-bold" : "border-[#6E6E6E]/30 text-[#6E6E6E]"}`}>
            {warm ? "● Warm" : "◇ Cold"}
          </span>
        )}
      </div>
    </div>
  );
}
